<template>
  <Card title="Transform Options" class="fill-height">
    <template slot="header-suffix">
      <b-dropdown size="sm" text="Actions" v-b-tooltip="tooltip">
        <b-dropdown-item-button
          :disabled="rows.length == 1 && lastRowIsEmpty()"
          @click="resetTransform"
        >
          Clear
        </b-dropdown-item-button>
        <b-dropdown-item-button
          :disabled="!sourceFields.length"
          @click="fillFromSource"
        >
          Fill source
        </b-dropdown-item-button>
        <b-dropdown-item-button
          :disabled="!targetFields.length"
          @click="fillFromTarget"
        >
          Fill target
        </b-dropdown-item-button>
      </b-dropdown>
    </template>
    <!-- TODO: Should switch this out depending on type of transform -->
    <b-alert
      v-show="hasSourceData && !dataMatchesCurrentRead"
      show
      variant="warning"
    >
      <pre class="pre-wrap m-0">
              The data displayed was run from a different query than the current one
            </pre
      >
    </b-alert>
    <b-container fluid class="mb-5">
      <b-row align-h="around">
        <h6 class="mr-4">Source Value</h6>
        <h6 class="mr-5">New Field</h6>
      </b-row>
      <b-row
        v-for="(item, index) in rows"
        :key="`simple-transform-row__${index}`"
      >
        <b-input-group size="sm" class="my-1">
          <autocomplete
            v-model="item[1]"
            :items="sourceFields"
            size="sm"
            @blur="rowBlur(index)"
          />
          <div class="input-group-prepend input-group-append">
            <div class="input-group-text">
              <i class="bi bi-arrow-right" />
            </div>
          </div>

          <autocomplete
            v-model="item[0]"
            :items="targetFields"
            size="sm"
            @blur="rowBlur(index)"
          />
          <b-button
            size="sm ml-2"
            variant="light"
            @click="remove(index)"
            tabindex="-1"
            :class="removable(index) ? '' : 'hide'"
            :disabled="!removable(index)"
          >
            <i class="bi bi-x" />
          </b-button>
        </b-input-group>
      </b-row>
    </b-container>
  </Card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import isEmpty from 'lodash/isEmpty';

import Card from '@/components/Card.vue';
import store from '@/plugins/vuex';
import Autocomplete, { AutocompleteItem } from '@/components/Autocomplete.vue';

export default defineComponent({
  name: 'SimpleTransformForm',
  components: {
    Autocomplete,
    Card,
  },
  data() {
    const map = store.state.transform.options?.map || {};
    return {
      map,
      rows: Object.entries(isEmpty(map) ? { '': '' } : map),
    };
  },
  computed: {
    ...mapState({
      storeMap: (state: any) => state.transform.options?.map,
      hasSourceData: (state: any) => state.read.data?.length,
    }),
    ...mapGetters({
      dataMatchesCurrentRead: 'read/dataMatchesCurrentRead',
      rawSourceFields: 'transform/sourceAvailableFields',
      targetFields: 'transform/targetAvailableFields',
    }),
    sourceFields(): AutocompleteItem[] {
      return this.rawSourceFields.map((field: any) => ({
        ...field,
        selected: this.sourceValueIsMapped(field.value),
      }));
    },
    tooltip() {
      const tooltip = {
        placement: 'left',
      };

      if (!this.rawSourceFields && !this.targetFields.length) {
        tooltip['title'] =
          'To fill source you need to run a query and to fill target you need to connect to an Aison server';
      } else if (!this.rawSourceFields.length) {
        tooltip['title'] = 'To fill source you need to run a query';
      } else if (!this.targetFields.length) {
        tooltip['title'] =
          'To fill target you need to connect to an Aison server';
      } else {
        tooltip['disabled'] = true;
      }
      return tooltip;
    },
  },
  methods: {
    ...mapActions({
      resetTransform: 'transform/reset',
    }),
    rowBlur(index) {
      if (index != this.rows.length - 1 && this.rowIsEmpty(this.rows[index])) {
        this.remove(index);
      }
    },
    loadRowsFromStore() {
      this.map = store.state.transform.options.map || { '': '' };
      // TODO: Fix this when changing configs
      // this.map = isEmpty(store.state.transform.options.map)
      //   ? store.state.transform.options.map
      //   : { '': '' };
      this.rows = Object.entries(this.map);
    },
    setRowsInStore() {
      this.map = Object.fromEntries(this.rows.filter(([k, v]) => k || v));
      store.commit.transform.setOptions({ map: this.map });
    },
    removable(index) {
      const row = this.rows[index];
      if (
        this.rows.length < 2 ||
        (index === this.rows.length - 1 && this.rowIsEmpty(row))
      ) {
        return false;
      }
      return true;
    },
    remove(index) {
      // The rows handler will update the store
      delete this.rows[index];
    },
    rowIsEmpty(row) {
      return !row[0] && !row[1];
    },
    lastRow() {
      return this.rows[this.rows.length - 1];
    },
    lastRowIsEmpty() {
      return this.rowIsEmpty(this.lastRow());
    },
    fillFromSource() {
      const newRows = this.sourceFields.map<[string, string]>(f => [
        '',
        f.value,
      ]);
      if (this.lastRowIsEmpty()) {
        this.remove(this.rows.length - 1);
      }
      this.rows.push(...newRows);
    },
    fillFromTarget() {
      const newRows = this.targetFields.map(f => [f.value, '']);
      if (this.lastRowIsEmpty()) {
        this.remove(this.rows.length - 1);
      }
      this.rows.push(...newRows);
    },
    sourceValueIsMapped(key) {
      return !!this.rows.find(r => r[1] === key);
    },
  },
  watch: {
    rows: {
      deep: true,
      immediate: true,
      handler() {
        if (!this.lastRowIsEmpty()) {
          this.rows.push(['', '']);
        }
        this.setRowsInStore();
      },
    },
    storeMap() {
      if (store.state.transform.options.map != this.map) {
        // If the map was changed outside of this component it will be a
        // different map
        this.loadRowsFromStore();
      }
    },
  },
});
</script>
<style scoped>
.hide {
  opacity: 0 !important;
}
</style>
